import { ArrowDownIcon, ArrowUpIcon, CaretDownIcon } from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu'
import * as RadixRadioGroup from '@radix-ui/react-radio-group'
import { OutlineButton } from '../buttons/outline-button'
import { RadioItem } from '../input/radio'
import * as styles from './sort-dropdown-menu.css'

export type SortOption<T = string> = {
  value: T
  label: string
}

export type SortDropdownMenuProps<T = string> = {
  options: SortOption<T>[]
  selectedValue: T
  onSortChange: (value: T) => unknown
  isAscending: boolean
  onSortDirectionChange: (isAscending: boolean) => unknown
  className?: string
  placeholder?: string
}

export const SortDropdownMenu = <T extends string>({
  options,
  selectedValue,
  onSortChange,
  isAscending,
  onSortDirectionChange,
  className,
  placeholder = 'Sort by',
}: SortDropdownMenuProps<T>) => {
  const selectedOption = options.find(option => option.value === selectedValue)
  const displayLabel = selectedOption ? `${placeholder}: ${selectedOption.label}` : placeholder

  const handleArrowClick = () => {
    onSortDirectionChange(!isAscending)
  }

  return (
    <RadixDropdownMenu.Root>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <RadixDropdownMenu.Trigger asChild>
          <OutlineButton state="inactive" size="small" className={className} aria-label="Sort by">
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {displayLabel}
              <CaretDownIcon
                size={12}
                weight="light"
                style={{
                  color: mappedColors.neutral.halo,
                  flexShrink: 0,
                }}
              />
            </span>
          </OutlineButton>
        </RadixDropdownMenu.Trigger>
        <button
          onClick={handleArrowClick}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-label={isAscending ? 'Sort ascending' : 'Sort descending'}
        >
          {isAscending ? (
            <ArrowUpIcon
              size={16}
              weight="light"
              style={{
                color: mappedColors.neutral.halo,
                flexShrink: 0,
              }}
            />
          ) : (
            <ArrowDownIcon
              size={16}
              weight="light"
              style={{
                color: mappedColors.neutral.halo,
                flexShrink: 0,
              }}
            />
          )}
        </button>
      </div>

      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content className={styles.dropdownContent} sideOffset={5}>
          <RadixRadioGroup.Root
            value={selectedValue}
            onValueChange={value => onSortChange(value as T)}
            className={styles.radioGroup}
          >
            {options.map(option => (
              <RadioItem key={option.value} value={option.value} label={option.label} id={`${option.value}-radio`} />
            ))}
          </RadixRadioGroup.Root>
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  )
}
